import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';

/**
 * ComparisonContext — a small cross-page "comparison tray".
 *
 * Users add claims/findings from anywhere (Research Fronts evidence, front cards,
 * later: graph nodes, digest claims) via "+ add to comparison". The tray persists
 * to localStorage so the selection survives tab switches, and is sent to the
 * Claim Adjudicator (`POST /api/rag/adjudicate`) which renders the verdicts as an
 * answer in the RAG/Ask panel. Capped at 6 (the adjudicator's ADJ_MAX_ASSERTIONS).
 *
 * Each item: { text: string (the assertion), label?: string, article_id?: number }.
 */
const STORAGE_KEY = 'mvkg:comparison-items';
const MAX_ITEMS = 6;

const ComparisonContext = createContext(null);

export const ComparisonProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      const parsed = v ? JSON.parse(v) : [];
      return Array.isArray(parsed) ? parsed.slice(0, MAX_ITEMS) : [];
    } catch (_) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_) { /* quota / private mode */ }
  }, [items]);

  const _key = (t) => String(t || '').trim().slice(0, 200).toLowerCase();

  const addItem = useCallback((item) => {
    const text = String(item?.text || '').trim();
    if (!text) return false;
    let added = false;
    setItems((prev) => {
      if (prev.length >= MAX_ITEMS) return prev;
      if (prev.some((p) => _key(p.text) === _key(text))) return prev;
      added = true;
      return [...prev, {
        text,
        label: item.label ? String(item.label) : '',
        article_id: item.article_id ?? null,
      }];
    });
    return added;
  }, []);

  const removeItem = useCallback((idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback((text) => {
    const k = _key(text);
    return items.some((p) => _key(p.text) === k);
  }, [items]);

  return (
    <ComparisonContext.Provider value={{ items, addItem, removeItem, clear, has, max: MAX_ITEMS }}>
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => (
  useContext(ComparisonContext) || {
    items: [], addItem: () => false, removeItem: () => {}, clear: () => {}, has: () => false, max: MAX_ITEMS,
  }
);
